import React, { useCallback, useMemo, useContext, useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Button,
  IconButton,
  Menu,
  MenuItem,
  TableSortLabel,
  Tooltip,
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  Chip
} from '@mui/material';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import { styled } from '@mui/material/styles';
import SettingsIcon from '@mui/icons-material/Settings';
import DeleteIcon from '@mui/icons-material/Delete';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { doc, deleteDoc, addDoc, collection, query, where, getDocs, updateDoc } from 'firebase/firestore';
import { format, parseISO, differenceInCalendarDays } from 'date-fns';
import { db } from '../firebaseConfig';
import ConfirmationDialog from '../components/ConfirmationDialog';
import { useGenerateTasksForHarvest } from '../utils/harvestTaskGenerator';
import InfoModalV2 from '../components/InfoModalV2';
import { createHarvestSchema } from '../modals/HarvestSchema';
import { AppContext } from '../AppContext';
import AddHarvest from '../components/AddHarvest';
import { SnackbarContext } from '../SnackbarContext';
import useIsMobile from '../utils/useIsMobile';

import { trackFeature } from '../utils/analytics';

function Harvests() {
  const { user, harvests, seeds, trays, userData, farmData } = useContext(AppContext);
  const activeFarmId = userData?.activeFarmId;

  // Local states for dialog, sorting, menu, pagination, and harvest type.
  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [harvestToEdit, setHarvestToEdit] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedHarvest, setSelectedHarvest] = useState(null);
  const [harvestsSortColumn, setHarvestsSortColumn] = useState('startDate');
  const [harvestsSortDirection, setHarvestsSortDirection] = useState('asc');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmHarvest, setConfirmHarvest] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const [selectedHarvestData, setSelectedHarvestData] = useState(null);
  const generateTasksForHarvest = useGenerateTasksForHarvest();

  const { showSuccess, showError } = useContext(SnackbarContext);

  // State for “mark as harvested” confirmation
const [archiveConfirmOpen, setArchiveConfirmOpen] = useState(false);
const [harvestToArchive, setHarvestToArchive] = useState(null);

// State for deleting an archived harvest
const [deleteArchivedConfirmOpen, setDeleteArchivedConfirmOpen] = useState(false);
const [harvestToDeleteArchived, setHarvestToDeleteArchived] = useState(null);

  const defaultDateFormat = farmData?.settings?.dateFormat || 'dd-MM-yyyy';
  const isMobile = useIsMobile();
  const formatDate = useCallback((value) => {
    if (!value) return '';
    try {
      return format(parseISO(value), defaultDateFormat);
    } catch (error) {
      return value;
    }
  }, [defaultDateFormat]);

  const handleRowClick = (harvest) => {
    // 1) Base transform
    const transformedHarvest = {
      ...harvest,
      startDate: harvest.startDate,
      harvestDate: harvest.harvestDate,
      seeds: getSeedDisplay(harvest),
      trays: getTrayDisplay(harvest),
      portions: getPortions(harvest),
      yieldDisplay:
        harvest.yieldValue && harvest.yieldUnit
          ? `${harvest.yieldValue} ${harvest.yieldUnit}`
          : '',
    };

    // 2) Look up the seed’s data
    const firstSeedId =
      (harvest.seeds && harvest.seeds[0]?.seedId) ||
      harvest.seedId;
    const seedData = seedLookup[firstSeedId] || {};

    // 3) Inject your new seed‐derived fields, including watering
    transformedHarvest.soakHours = seedData.soakHours ?? '';
    transformedHarvest.blackoutDays = seedData.blackout ?? '';
    transformedHarvest.germinationDays = seedData.germination ?? '';
    transformedHarvest.wateringFrequency = seedData.wateringFrequency ?? '';
    transformedHarvest.wateringIntervalUnit = seedData.wateringIntervalUnit ?? '';

    // 4) Fire the modal
    setSelectedHarvestData(transformedHarvest);
    setInfoModalOpen(true);
  };


  // Build lookup for seeds by id.
  const seedLookup = useMemo(() => {
    return seeds.reduce((lookup, seed) => {
      lookup[seed.id] = seed;
      return lookup;
    }, {});
  }, [seeds]);

  // Build lookup for trays by id.
  const trayLookup = useMemo(() => {
    return trays.reduce((lookup, tray) => {
      lookup[tray.id] = tray;
      return lookup;
    }, {});
  }, [trays]);

  // Update sortedHarvests so that sorting by seeds and portions reads from the seeds array.
  const sortedHarvests = useMemo(() => {
    const data = [...harvests];
    data.sort((a, b) => {
      let aValue, bValue;
      if (harvestsSortColumn === 'startDate') {
        const dateA = a.startDate ? parseISO(a.startDate) : new Date(0);
        const dateB = b.startDate ? parseISO(b.startDate) : new Date(0);
        return harvestsSortDirection === 'asc' ? dateA - dateB : dateB - dateA;
      } else if (harvestsSortColumn === 'trays') {
        const trayIdA = a.trayId || (a.seeds && a.seeds[0]?.trayId);
        const trayIdB = b.trayId || (b.seeds && b.seeds[0]?.trayId);
        aValue = trayIdA && trayLookup[trayIdA] ? trayLookup[trayIdA].brand || '' : '';
        bValue = trayIdB && trayLookup[trayIdB] ? trayLookup[trayIdB].brand || '' : '';
        return harvestsSortDirection === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      } else if (harvestsSortColumn === 'seeds') {
        const getSeedStr = (harvest) => {
          if (harvest.seeds && harvest.seeds.length > 0) {
            return harvest.seeds
              .map(s => (seedLookup[s.seedId]?.plants?.map(p => p.name).join('-') || 'unknown'))
              .join(', ');
          }
          return 'unknown';
        };
        aValue = getSeedStr(a);
        bValue = getSeedStr(b);
        return harvestsSortDirection === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      } else if (harvestsSortColumn === 'yieldValue') {
        // Convert yieldValue to a number for proper numerical sorting
        aValue = parseFloat(a.yieldValue) || 0;
        bValue = parseFloat(b.yieldValue) || 0;
        return harvestsSortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      } else if (harvestsSortColumn === 'portions') {
        aValue = a.portions || (a.seeds && a.seeds[0]?.portions) || '';
        bValue = b.portions || (b.seeds && b.seeds[0]?.portions) || '';
        return harvestsSortDirection === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      } else if (harvestsSortColumn === 'portionsLeft') {
        const aNum = parseFloat(a.portionsLeft) || 0;
        const bNum = parseFloat(b.portionsLeft) || 0;
        return harvestsSortDirection === 'asc' ? aNum - bNum : bNum - aNum;
      }
      return 0;
    });
    return data;
  }, [harvests, harvestsSortColumn, harvestsSortDirection, seedLookup, trayLookup]);


// Active (not yet harvested)
const activeHarvests = useMemo(
  () => sortedHarvests.filter((h) => !h.harvested),
  [sortedHarvests]
);

// Archived (harvested = true)
const archivedHarvests = useMemo(
  () => sortedHarvests.filter((h) => h.harvested),
  [sortedHarvests]
);

// Paginate only active
const paginatedHarvests = useMemo(() => {
  const startIndex = page * rowsPerPage;
  return activeHarvests.slice(startIndex, startIndex + rowsPerPage);
}, [activeHarvests, page, rowsPerPage]);

  // Handlers for dialog and actions.
  const handleOpen = () => {
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setOpen(false);
    setEditMode(false);
    setHarvestToEdit(null);
  };
  const handleAddHarvest = () => {
    handleCloseDialog();
  };

  const handleClone = async () => {
    if (!selectedHarvest || !user) return;
    if (!activeFarmId) {
      showError?.('No active farm selected.');
      return;
    }

    trackFeature('harvest_clone_initiated', { sourceId: selectedHarvest.id });

    let newHarvestId;
    try {
      // 1) Clone the harvest itself
      const { id, ...dataToClone } = selectedHarvest;
      const cloneWithDate = {
        ...dataToClone,
        startDate: selectedHarvest.startDate,
        portionsLeft: Number(dataToClone.portions),
      };
      const cloneRef = await addDoc(
        collection(db, 'farms', activeFarmId, 'harvests'),
        cloneWithDate
      );
      newHarvestId = cloneRef.id;
      showSuccess?.('Harvest cloned successfully.');
    } catch (err) {
      showError?.('Failed to clone harvest.');
      handleMenuClose();
      return;
    }

    // 2) Now generate & write the tasks
    try {
      const seedId = selectedHarvest.seedId;
      const seedData = seeds.find((s) => s.id === seedId) || {};
      const seedName = seedData?.name || 'Unknown';

      const tasks = generateTasksForHarvest({
        harvestId: newHarvestId,
        seedName,
        startDate: selectedHarvest.startDate,
        germinationDays: Number(seedData.germination) || 0,
        blackoutDays: Number(seedData.blackout) || 0,
        growthDays: Number(selectedHarvest.growth) || 0,
        wateringFrequency: Number(seedData.wateringFrequency) || 1,
        wateringIntervalUnit: seedData.wateringIntervalUnit || 'days',
        dryDay: Boolean(seedData.dryDay),
      });

      console.log('Cloning tasks for harvest', newHarvestId, tasks);

      const tasksRef = collection(db, 'farms', activeFarmId, 'tasks');
      await Promise.all(tasks.map((t) => addDoc(tasksRef, t)));
      showSuccess?.('Associated tasks created successfully.');
    } catch (err) {
      showError?.('Harvest cloned but failed to generate tasks.');
    } finally {
      handleMenuClose();
    }
    trackFeature('harvest_cloned', { sourceId: selectedHarvest.id, newId: newHarvestId });
  };

  const handleDelete = async (id) => {
    if (!user) return;
    if (!activeFarmId) {
      showError?.('No active farm selected.');
      return;
    }

    try {
      const tasksQuery = query(
        collection(db, 'farms', activeFarmId, 'tasks'),
        where('harvestId', '==', id)
      );
      const querySnapshot = await getDocs(tasksQuery);

      const deleteTaskPromises = querySnapshot.docs.map((taskDoc) =>
        deleteDoc(taskDoc.ref)
      );
      await Promise.all(deleteTaskPromises);

      await deleteDoc(doc(db, 'farms', activeFarmId, 'harvests', id));

      showSuccess?.('Harvest and associated tasks deleted.');
    } catch (error) {
      showError?.('Failed to delete harvest and associated tasks.');
    } finally {
      handleMenuClose();
    }
  };

  const openConfirmDialog = (harvest) => {
    setConfirmHarvest(harvest);
    setConfirmOpen(true);
  };
  const handleConfirmDelete = async () => {
    if (confirmHarvest) {
      trackFeature('harvest_delete_initiated', { harvestId: confirmHarvest.id });
      await handleDelete(confirmHarvest.id);
      trackFeature('harvest_deleted', { harvestId: confirmHarvest.id });
    }
    setConfirmOpen(false);
    setConfirmHarvest(null);
  };
  const handleConfirmCancel = () => {
    setConfirmOpen(false);
    setConfirmHarvest(null);
  };
  const handleMenuOpen = (event, harvest) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedHarvest(harvest);
  };
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedHarvest(null);
  };
  const handleEdit = () => {
    if (!selectedHarvest) return;
    trackFeature('harvest_edit_open', { harvestId: selectedHarvest.id });
    setEditMode(true);
    setHarvestToEdit(selectedHarvest);
    handleOpen();
    handleMenuClose();
  };
  const handleHarvestsSort = (column) => {
    const isAsc = harvestsSortColumn === column && harvestsSortDirection === 'asc';
    setHarvestsSortDirection(isAsc ? 'desc' : 'asc');
    setHarvestsSortColumn(column);
  };
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const harvestSchema = useMemo(() => {
    return createHarvestSchema({
      formatDate,
      variant: 'full',
    });
  }, [formatDate]);

  const subscriptionActive = isSubscriptionActive(userData);

  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: 14,
    },
    '& .MuiTableSortLabel-root': {
      color: `${theme.palette.common.white} !important`,
      '&.Mui-active': {
        color: `${theme.palette.common.white} !important`,
      },
      '& .MuiTableSortLabel-icon': {
        color: `${theme.palette.common.white} !important`,
      },
      '&:hover': {
        color: 'rgba(255, 255, 255, 0.75) !important',
      },
    },
  }));

  // Helper: Format harvest date.
  const formatstartDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return format(parseISO(dateStr), defaultDateFormat);
    } catch (error) {
      console.error('Error parsing date:', dateStr, error);
      return dateStr;
    }
  };

  // Helper: Get tray display string from trayLookup.
  const getTrayDisplay = (harvest) => {
    // Prepend trayNumber if available.
    const trayPrefix = harvest.trayNumber ? `${harvest.trayNumber}x ` : '';

    // Try top-level trayId first, if not use the one inside seeds[0].
    const trayId = harvest.trayId || (harvest.seeds && harvest.seeds[0]?.trayId);
    if (trayId && trayLookup[trayId]) {
      const t = trayLookup[trayId];
      if (t.length && t.width) {
        return trayPrefix + `${t.brand} - ${t.length}${t.lengthUnit} x ${t.width}${t.widthUnit}`;
      } else {
        return trayPrefix + (t.brand || 'N/A');
      }
    }
    return trayPrefix + (harvest.trays || 'N/A');
  };


  // Helper: Get seed display string from seedLookup.
  const getSeedDisplay = (harvest) => {
    if (harvest.seeds && harvest.seeds.length > 0) {
      // For blends, join the names with a comma.
      return harvest.seeds
        .map(s => (s.seedId && seedLookup[s.seedId] ? seedLookup[s.seedId].name : 'Unknown'))
        .join('/');
    }
    // Fallback to legacy field if exists.
    if (harvest.seedId && seedLookup[harvest.seedId]) {
      return seedLookup[harvest.seedId].name || 'Unknown';
    }
    return 'Unknown';
  };

  // Helper: Get portions.
  const getPortions = (harvest) => {
    if (harvest.portions) return harvest.portions;
    if (harvest.seeds && harvest.seeds[0]?.portions) return harvest.seeds[0].portions;
    return '';
  };

  // Helper: Get portions left.
  const getPortionsLeft = (harvest) => {
    // if the document has the new field, use it…
    if (harvest.portionsLeft != null) return String(harvest.portionsLeft);
    // else fall back to legacy
    if (harvest.seeds && harvest.seeds[0]?.portionsLeft)
      return String(harvest.seeds[0].portionsLeft);
    return '';
  };

const handleConfirmArchive = async () => {
  if (!harvestToArchive) {
    trackFeature('harvest_archive_initiated', { harvestId: harvestToArchive.id });
    setArchiveConfirmOpen(false);
    return;
  }
  try {
    // 1) Delete all tasks with harvestId == this harvest’s id
    const tasksQuery = query(
      collection(db, 'farms', activeFarmId, 'tasks'),
      where('harvestId', '==', harvestToArchive.id)
    );
    const querySnapshot = await getDocs(tasksQuery);
    const deleteTaskPromises = querySnapshot.docs.map((docSnap) =>
      deleteDoc(docSnap.ref)
    );
    await Promise.all(deleteTaskPromises);

    // 2) Update the harvest document’s harvested flag to true
    await updateDoc(
      doc(db, 'farms', activeFarmId, 'harvests', harvestToArchive.id),
      { harvested: true }
    );
    showSuccess?.('Harvest marked as harvested and tasks deleted.');
    trackFeature('harvest_archived', { harvestId: harvestToArchive.id });
  } catch {
    showError?.('Failed to archive harvest.');
  } finally {
    setArchiveConfirmOpen(false);
    setHarvestToArchive(null);
  }
};

const handleCancelArchive = () => {
  setArchiveConfirmOpen(false);
  setHarvestToArchive(null);
};

const openDeleteArchivedConfirm = (harvest) => {
  setHarvestToDeleteArchived(harvest);
  setDeleteArchivedConfirmOpen(true);
};
const handleConfirmDeleteArchived = async () => {
  if (!harvestToDeleteArchived) {
    trackFeature('harvest_delete_archived_initiated', { harvestId: harvestToDeleteArchived.id });
    setDeleteArchivedConfirmOpen(false);
    return;
  }
  try {
    if (!activeFarmId) {
      showError?.('No active farm selected.');
      return;
    }
    // Just delete the harvest doc (tasks were already removed when it was archived)
    await deleteDoc(
      doc(db, 'farms', activeFarmId, 'harvests', harvestToDeleteArchived.id)
    );
    trackFeature('harvest_deleted_archived', { harvestId: harvestToDeleteArchived.id });
    showSuccess?.('Harvest record deleted.');
  } catch {
    showError?.('Failed to delete harvest record.');
  } finally {
    setDeleteArchivedConfirmOpen(false);
    setHarvestToDeleteArchived(null);
  }
};

const handleCancelDeleteArchived = () => {
  setDeleteArchivedConfirmOpen(false);
  setHarvestToDeleteArchived(null);
};


  return (
    <Box
      sx={{
        maxWidth: '80%',
        py: '3',
        backgroundColor: (theme) => theme.palette.background.default,
        color: (theme) => theme.palette.text.primary,
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        margin: '0 auto',
        padding: '15px',
        '@media (max-width: 600px)': { maxWidth: '100%' },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: (theme) => theme.palette.background.default,
          zIndex: -1,
        },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">Harvests</Typography>
        {/* Split Button */}

        <Button
          variant="contained"
          color="secondary"
   onClick={() => {
     trackFeature('harvest_add_open');
     handleOpen();
   }}
          sx={{ padding: '12px 24px' }}
          disabled={!subscriptionActive}
        >
          Add Harvest
        </Button>
      </Box>

      {isMobile && (
        <Paper sx={{ mb: 2 }}>
          <List disablePadding>
            {paginatedHarvests.map((harvest) => {
              let displayDate = harvest.startDate;
              let daysDiff = 0;
              if (harvest.harvestDate) {
                try {
                  const harvestDateObj = parseISO(harvest.harvestDate);
                  displayDate = formatstartDate(harvest.harvestDate);
                  daysDiff = differenceInCalendarDays(harvestDateObj, new Date());
                } catch (error) {
                  displayDate = harvest.harvestDate;
                }
              }
              const chipColor = daysDiff < 0 ? 'warning' : daysDiff === 0 ? 'secondary' : 'default';
              const seedDisplay = getSeedDisplay(harvest);
              const trayDisplay = getTrayDisplay(harvest);
              return (
                <React.Fragment key={harvest.id}>
                  <ListItem disablePadding>
                    <ListItemButton onClick={() => handleRowClick(harvest)} sx={{ alignItems: 'flex-start' }}>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Chip size="small" color={chipColor} label={displayDate || 'No date'} />
                            <Typography variant="subtitle2">{seedDisplay}</Typography>
                          </Box>
                        }
                        secondary={
                          <Box sx={{ mt: 0.5 }}>
                            <Typography variant="caption" sx={{ display: 'block' }}>
                              {trayDisplay}
                            </Typography>
                            <Typography variant="caption" sx={{ display: 'block' }}>
                              Portions {getPortions(harvest)} · Left {getPortionsLeft(harvest)}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                  <Divider />
                </React.Fragment>
              );
            })}
            {paginatedHarvests.length === 0 && (
              <ListItem>
                <ListItemText primary="No harvests found." />
              </ListItem>
            )}
          </List>
        </Paper>
      )}
      <TableContainer component={Paper} sx={{ display: { xs: 'none', sm: 'block' } }}>
        <Table>
          <TableHead>
            <TableRow>
              <StyledTableCell>
                <TableSortLabel
                  active={harvestsSortColumn === 'startDate'}
                  direction={harvestsSortColumn === 'startDate' ? harvestsSortDirection : 'asc'}
                  onClick={() => handleHarvestsSort('startDate')}
                >
                  Start Date
                </TableSortLabel>
              </StyledTableCell>
              <StyledTableCell>
                <TableSortLabel
                  active={harvestsSortColumn === 'harvestDate'}
                  direction={harvestsSortColumn === 'harvestDate' ? harvestsSortDirection : 'asc'}
                  onClick={() => handleHarvestsSort('harvestDate')}
                >
                  Harvest Date
                </TableSortLabel>
              </StyledTableCell>
              <StyledTableCell>
                <TableSortLabel
                  active={harvestsSortColumn === 'seeds'}
                  direction={harvestsSortColumn === 'seeds' ? harvestsSortDirection : 'asc'}
                  onClick={() => handleHarvestsSort('seeds')}
                >
                  Seeds
                </TableSortLabel>
              </StyledTableCell>
              <StyledTableCell>
                <TableSortLabel
                  active={harvestsSortColumn === 'trays'}
                  direction={harvestsSortColumn === 'trays' ? harvestsSortDirection : 'asc'}
                  onClick={() => handleHarvestsSort('trays')}
                >
                  Trays
                </TableSortLabel>
              </StyledTableCell>
              <StyledTableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TableSortLabel
                    active={harvestsSortColumn === 'totalYieldValue'}
                    direction={harvestsSortColumn === 'totalYieldValue' ? harvestsSortDirection : 'asc'}
                    onClick={() => handleHarvestsSort('totalYieldValue')}
                  >
                    Total Yield
                  </TableSortLabel>
                  <Tooltip
                    title={
                      <Typography sx={{ fontSize: '0.8rem' }}>
                        Derived by multiplying estimated yield by number of trays.
                      </Typography>
                    }
                  >
                    <HelpOutlineIcon fontSize="small" sx={{ cursor: 'pointer' }} />
                  </Tooltip>
                </Box>
              </StyledTableCell>
              <StyledTableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TableSortLabel
                    active={harvestsSortColumn === 'portions'}
                    direction={harvestsSortColumn === 'portions' ? harvestsSortDirection : 'asc'}
                    onClick={() => handleHarvestsSort('portions')}
                  >
                    Portions
                  </TableSortLabel>
                  <Tooltip
                    title={
                      <Typography sx={{ fontSize: '0.8rem' }}>
                        Derived by dividing the total estimated yield by portion size.
                      </Typography>
                    }
                  >
                    <HelpOutlineIcon fontSize="small" sx={{ cursor: 'pointer' }} />
                  </Tooltip>
                </Box>
              </StyledTableCell>
              <StyledTableCell>
                <TableSortLabel
                  active={harvestsSortColumn === 'portionsLeft'}
                  direction={harvestsSortColumn === 'portionsLeft' ? harvestsSortDirection : 'asc'}
                  onClick={() => handleHarvestsSort('portionsLeft')}
                >
                  Portions Left
                </TableSortLabel>
              </StyledTableCell>

              <StyledTableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>Notes</span>
                  <Tooltip
                    title={
                      <Typography sx={{ fontSize: '0.8rem' }}>
                        Mouse over note to read entire entry.
                      </Typography>
                    }
                  >
                    <HelpOutlineIcon fontSize="small" sx={{ cursor: 'pointer' }} />
                  </Tooltip>
                </Box>
              </StyledTableCell>
              <StyledTableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>Harvested</span>
                  <Tooltip
                    title={
                      <Typography sx={{ fontSize: '0.8rem' }}>
                        Visual indication of harvest status, please complete associated tasks and delete entry as appropriate.
                      </Typography>
                    }
                  >
                    <HelpOutlineIcon fontSize="small" sx={{ cursor: 'pointer' }} />
                  </Tooltip>
                </Box>
              </StyledTableCell>

              <StyledTableCell>Actions</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedHarvests.map((harvest) => {
              const displayDate = formatstartDate(harvest.startDate);
              const seedDisplay = getSeedDisplay(harvest);
              const trayDisplay = getTrayDisplay(harvest);
              const portionsDisplay = getPortions(harvest);
              // Compute daysDiff based on harvestDate
              let daysDiff = 0;
              if (harvest.harvestDate) {
                try {
                  const harvestDateObj = parseISO(harvest.harvestDate);
                  daysDiff = differenceInCalendarDays(harvestDateObj, new Date());
                } catch (error) {
                  console.error('Error parsing harvestDate for harvest', harvest.id, error);
                }
              }
              return (
                <TableRow
                  key={harvest.id}
                  onClick={() => handleRowClick(harvest)}
                  sx={(theme) => ({
                    cursor: 'pointer',
                    backgroundColor: (() => {
                      if (!harvest.harvestDate) return 'inherit';
                      if (daysDiff < 0) {
                        return theme.palette.mode === 'dark'
                          ? theme.palette.warning.dark
                          : theme.palette.warning.lighter;
                      }
                      if (daysDiff === 0) {
                        return theme.palette.mode === 'dark'
                          ? theme.palette.secondary.dark
                          : theme.palette.secondary.lighter;
                      }
                      return 'inherit';
                    })(),

                  })}
                >
                  <TableCell>{displayDate}</TableCell>
                  <TableCell>{harvest.harvestDate ? formatstartDate(harvest.harvestDate) : ''}</TableCell>
                  <TableCell>{seedDisplay}</TableCell>
                  <TableCell>{trayDisplay}</TableCell>
                  <TableCell>
                    {harvest.totalYieldValue != null
                      ? (() => {
                        const val = harvest.totalYieldValue;
                        const unit = harvest.yieldUnit;
                        if (unit === 'g') {
                          return val >= 1000
                            ? `${(val / 1000).toFixed(2)} kg`
                            : `${val} g`;
                        }
                        if (unit === 'oz') {
                          return val >= 16
                            ? `${(val / 16).toFixed(2)} lb`
                            : `${val} oz`;
                        }
                        return `${val} ${unit}`;
                      })()
                      : ''}
                  </TableCell>

<TableCell>
  {(() => {
    const raw = parseFloat(portionsDisplay);
    return Number.isFinite(raw) ? raw.toFixed(2) : portionsDisplay;
  })()}
</TableCell>

                  <TableCell>
                    {(() => {
                      const raw = parseFloat(getPortionsLeft(harvest));
                      return Number.isFinite(raw) ? raw.toFixed(2) : '';
                    })()}
                  </TableCell>


                  <TableCell
                    sx={{
                      maxWidth: 150,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <Tooltip title={<Typography sx={{ fontSize: '0.8rem' }}>{harvest.notes || ''}</Typography>}>
                      <Box
                        component="span"
                        sx={{
                          display: 'inline-block',
                          width: '100%',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {harvest.notes || ''}
                      </Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
<Checkbox
  checked={!!harvest.harvested}
  onChange={(e) => {
    e.stopPropagation();
    // If user is ticking it true, open confirm
    if (e.target.checked) {
      setHarvestToArchive(harvest);
      setArchiveConfirmOpen(true);
    }
    // If they're unticking (i.e. setting harvested=false), update immediately
    else {
      (async () => {
        if (!activeFarmId) {
          showError?.('No active farm selected.');
          return;
        }
        try {
          await updateDoc(
            doc(db, 'farms', activeFarmId, 'harvests', harvest.id),
            { harvested: false }
          );
          showSuccess?.('Harvest marked as not harvested.');
        } catch {
          showError?.('Failed to update harvest status.');
        }
      })();
    }
  }}
  color="primary"
/>

                  </TableCell>

                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMenuOpen(e, harvest);
                      }}
                    >
                      <SettingsIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
{paginatedHarvests.length === 0 && (
  <TableRow>
    <TableCell colSpan={8} align="center">
      No active harvests found.
    </TableCell>
  </TableRow>
)}
          </TableBody>

        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={sortedHarvests.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

<Box sx={{ mt: 4 }}>
  <Typography variant="h5" sx={{ mb: 1 }}>
    Harvested
  </Typography>
  {isMobile && (
    <Paper sx={{ mb: 2 }}>
      <List disablePadding>
        {archivedHarvests.map((harvest) => {
          const displayDate = formatstartDate(harvest.startDate);
          const seedDisplay = getSeedDisplay(harvest);
          const trayDisplay = getTrayDisplay(harvest);
          return (
            <React.Fragment key={harvest.id}>
              <ListItem disablePadding>
                <ListItemButton onClick={() => handleRowClick(harvest)} sx={{ alignItems: 'flex-start' }}>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Chip size="small" color="primary" label={displayDate || 'No date'} />
                        <Typography variant="subtitle2">{seedDisplay}</Typography>
                      </Box>
                    }
                    secondary={
                      <Box sx={{ mt: 0.5 }}>
                        <Typography variant="caption" sx={{ display: 'block' }}>
                          {trayDisplay}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block' }}>
                          Portions {getPortions(harvest)} · Left {getPortionsLeft(harvest)}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItemButton>
              </ListItem>
              <Divider />
            </React.Fragment>
          );
        })}
        {archivedHarvests.length === 0 && (
          <ListItem>
            <ListItemText primary="No harvested records." />
          </ListItem>
        )}
      </List>
    </Paper>
  )}
  <TableContainer component={Paper} sx={{ display: { xs: 'none', sm: 'block' } }}>
    <Table size="small">
<TableHead>
  <TableRow>
    <StyledTableCell>Start Date</StyledTableCell>
    <StyledTableCell>Harvest Date</StyledTableCell>
    <StyledTableCell>Seeds</StyledTableCell>
    <StyledTableCell>Trays</StyledTableCell>
    <StyledTableCell>Total Yield</StyledTableCell>
    <StyledTableCell>Portions</StyledTableCell>
    <StyledTableCell>Portions Left</StyledTableCell>
    <StyledTableCell>Notes</StyledTableCell>
    {/* New “Delete” column header */}
    <StyledTableCell>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <span>Delete</span>
        <Tooltip
          title={
            <Typography sx={{ fontSize: '0.8rem' }}>
              Permanently delete this harvest record.
            </Typography>
          }
        >
          <HelpOutlineIcon fontSize="small" sx={{ cursor: 'pointer' }} />
        </Tooltip>
      </Box>
    </StyledTableCell>
  </TableRow>
</TableHead>

      <TableBody>
        {archivedHarvests.map((harvest) => {
          const displayDate = formatstartDate(harvest.startDate);
          const seedDisplay = getSeedDisplay(harvest);
          const trayDisplay = getTrayDisplay(harvest);
          const portionsDisplay = getPortions(harvest);
          // No need for daysDiff coloring: every row here is harvested,
          // so just use primary light background
          return (
<TableRow
  key={harvest.id}
  onClick={() => handleRowClick(harvest)}
  sx={(theme) => ({
    cursor: 'pointer',
    backgroundColor:
      theme.palette.mode === 'dark'
        ? theme.palette.primary.dark
        : theme.palette.primary.lighter,
  })}
>


  
              <TableCell>{displayDate}</TableCell>
              <TableCell>
                {harvest.harvestDate ? formatstartDate(harvest.harvestDate) : ''}
              </TableCell>
              <TableCell>{seedDisplay}</TableCell>
              <TableCell>{trayDisplay}</TableCell>
              <TableCell>
                {harvest.totalYieldValue != null
                  ? (() => {
                      const val = harvest.totalYieldValue;
                      const unit = harvest.yieldUnit;
                      if (unit === 'g') {
                        return val >= 1000
                          ? `${(val / 1000).toFixed(2)} kg`
                          : `${val} g`;
                      }
                      if (unit === 'oz') {
                        return val >= 16
                          ? `${(val / 16).toFixed(2)} lb`
                          : `${val} oz`;
                      }
                      return `${val} ${unit}`;
                    })()
                  : ''}
              </TableCell>
              <TableCell>
                {(() => {
                  const raw = parseFloat(portionsDisplay);
                  return Number.isFinite(raw) ? raw.toFixed(2) : portionsDisplay;
                })()}
              </TableCell>
              <TableCell>
                {(() => {
                  const raw = parseFloat(getPortionsLeft(harvest));
                  return Number.isFinite(raw) ? raw.toFixed(2) : '';
                })()}
              </TableCell>
              <TableCell
                sx={{
                  maxWidth: 150,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                <Tooltip title={harvest.notes || ''}>
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-block',
                      width: '100%',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {harvest.notes || ''}
                  </Box>
                </Tooltip>
              </TableCell>
<TableCell onClick={(e) => e.stopPropagation()}>
  <IconButton
    color="inherit"
    size="small"
    onClick={(e) => {
      e.stopPropagation();
      openDeleteArchivedConfirm(harvest);
    }}
        sx={(theme) => ({
      color:
        theme.palette.mode === 'light'
          ? theme.palette.error.dark
          : theme.palette.error.main,
    })}
  >
    <DeleteIcon />
  </IconButton>
</TableCell>

            </TableRow>
          );
        })}
        {archivedHarvests.length === 0 && (
          <TableRow>
            <TableCell colSpan={9} align="center">
              No harvested records.
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  </TableContainer>
</Box>


      <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={handleMenuClose}>
        <MenuItem disabled={!subscriptionActive} onClick={handleEdit}>
          Edit
        </MenuItem>
        <MenuItem disabled={!subscriptionActive} onClick={handleClone}>
          Clone
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            openConfirmDialog(selectedHarvest);
          }}
        >
          Delete
        </MenuItem>
      </Menu>

      <AddHarvest
        open={open}
        onClose={handleCloseDialog}
        onAdd={handleAddHarvest}
        editMode={editMode}
        harvestToEdit={harvestToEdit}
        setSuccess={showSuccess}
        setError={showError}
      />

      <ConfirmationDialog
        open={confirmOpen}
        title="Confirm Harvest Deletion"
        message="Are you sure you want to delete this harvest? This will also remove any associated tasks."
        onConfirm={handleConfirmDelete}
        onClose={handleConfirmCancel}
      />

      <InfoModalV2
        open={infoModalOpen}
        onClose={() => setInfoModalOpen(false)}
        data={selectedHarvestData}
        schema={harvestSchema}
      />

      <ConfirmationDialog
  open={archiveConfirmOpen}
  title="Confirm Mark as Harvested"
  message="Are you sure you want to mark this as harvested? All associated tasks will be deleted."
  onConfirm={handleConfirmArchive}
  onClose={handleCancelArchive}
/>

<ConfirmationDialog
  open={deleteArchivedConfirmOpen}
  title="Delete Harvested Record"
  message="Are you sure you want to permanently delete this harvested record?"
  onConfirm={handleConfirmDeleteArchived}
  onClose={handleCancelDeleteArchived}
/>


    </Box>
  );
}

export default Harvests;

function isSubscriptionActive(userData) {
  if (!userData || !userData.accessExpires) {
    return true;
  }
  const now = new Date();
  const expires = userData.accessExpires.toDate
    ? userData.accessExpires.toDate()
    : new Date(userData.accessExpires);
  return now <= expires;
}



